import { cloneDeep } from 'lodash-es'
import {
  removeObjKeys,
  isValidHttpsImageUrl,
  isValidHttpsUrl,
  isValidEmail,
  isValidZip,
  isValidPhone,
  isValidTaxId,
  isValidRoutingNumber,
  isValidAccountNumber,
  isValidOrderNumber,
} from './validator.util'

export function removeUnusedFields(formData, fieldsToRemove = []) {
  // clone formData to avoid mutating original object
  return removeObjKeys(cloneDeep(formData), ['dateCreated', 'dateUpdated', 'dateModified', ...fieldsToRemove])
}

/**
 * Normalize an optional numeric form input. `v-model.number` leaves a cleared
 * field as '' (and range rules can pass it via '' >= 0 coercion), so use this
 * before emitting/saving to keep an empty input out of the payload as a real
 * null. Non-finite values (unparseable strings) also collapse to null.
 * @param {string|number|null|undefined} val
 * @returns {number|null}
 */
export function numberOrNull(val) {
  if (val === '' || val == null) return null
  const n = Number(val)
  return Number.isFinite(n) ? n : null
}

export function imageUrlField(v) {
  return !v || isValidHttpsImageUrl(v) || 'Image URL must begin with https://. Accepted formats: jpg, jpeg, png, ico, svg'
}

export function urlField(v) {
  return !v || isValidHttpsUrl(v) || 'Must be a fully qualified URL from a secure domain.'
}

export function emailAddressField(v) {
  return !v || isValidEmail(v) || 'Please enter a valid email'
}

export function phoneNumberField(v) {
  return !v || isValidPhone(v) || 'Please enter a valid working phone number'
}

export function zipCodeField(v) {
  return !v || isValidZip(v) || 'Please enter a valid zip code'
}

export function orderNumberField(v) {
  return !v || isValidOrderNumber(v) || 'Order number must be at least 3 characters'
}

export function requiredField(fieldName = 'Input') {
  return v => !!v || `${fieldName} is required.`
}

/**
 * Like requiredField but also rejects whitespace-only input. Use for required
 * fields whose value is forwarded to a backend that enforces a non-empty
 * (post-trim) value — e.g. a custom vendor tag or a warehouse address line.
 * Without this, a value of "   " passes requiredField (truthy raw string) and
 * then submits as an effectively empty value, surfacing as a backend 400 or a
 * silently-blank record instead of an in-form validation message.
 * @param {string} [fieldName]
 */
export function requiredNonBlankField(fieldName = 'Input') {
  return v => (typeof v === 'string' ? v.trim().length > 0 : !!v) || `${fieldName} is required.`
}

export function taxIdField(v) {
  // Simple EIN format validation XX-XXXXXXX
  return !v || isValidTaxId(v) || 'Please enter a valid Tax ID (Format: XX-XXXXXXX)'
}

/**
 * Validate routing number using ABA routing number algorithm
 */
export function routingNumberField(v) {
  return !v || isValidRoutingNumber(v) || 'Invalid routing number - must be 9 digits and pass validation.'
}

/**
 * Validate account number
 */
export function accountNumberField(v) {
  return !v || isValidAccountNumber(v) || 'Invalid account number - should be between 4 and 17 digits.'
}
